import { ElementType, ReactNode, forwardRef, KeyboardEvent, MouseEvent } from 'react';
import { CSSObject, SerializedStyles } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { KeyNames } from '../../../../constants';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../../../type-utils';
import { ContextMenu, ContextMenuActions } from '../../../context-menu/context-menu';
import { SkeletonLoader, SpinningLoader } from '../../../loader';
import { Text } from '../../../text';
import { useChatBubbleContext } from '../../chat';
import { ChatStyles } from '../../chat.styles';
import { AiActivityBubble } from './ai-activity-bubble';
import { CallBubble } from './call-bubble';
import { ImageBubble } from './image-bubble';
import { PdfBubble } from './pdf-bubble';
import { TextBubble } from './text-bubble';
import { VideoBubble } from './video-bubble';

const IMAGE_SKELETON_SIZE = {
  width: 'min(70vw, 320px)',
  height: 'min(40vh, 480px)',
} as const;

export type BubbleProps = {
  text?: string | ReactNode;
  subText?: ReactNode;
  bubbleIcon?: ReactNode;
  onClick?: (e?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
  contextActions?: ContextMenuActions;
  contextMenuReturnFocus?: boolean;
  isDeleted?: boolean;
  styles?: SerializedStyles;
  variant: 'text' | 'deleted' | 'image' | 'call' | 'ai-activity' | 'pdf' | 'video';
  src: string;
  maxWidth: string;
  maxHeight: string;
  pdfProps?: {
    filename?: string;
    fileSizeBytes?: number;
    onClickPreview?: () => void;
    previewTrackingId?: string;
  };
  videoProps?: {
    onClickPreview?: () => void;
    previewTrackingId?: string;
  };
  imageProps?: {
    suppressPreview?: boolean;
    onLoad?: () => void;
    onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
    onClickDownload?: () => void;
    downloadImageTrackingId?: string;
    backgroundColor?: (typeof theme.colors)[keyof typeof theme.colors];
    css?: CSSObject;
  };
  imageLoadError?: boolean;
  callProps?: {
    callResult?: 'missed' | 'inbound' | 'outbound' | 'abandoned' | 'voicemail';
    callAction: () => void;
    playAction?: () => void;
  };
  leadingIcon?: ReactNode;
};

type PolymorphicBubbleProps<C extends ElementType = 'li'> = PolymorphicComponentPropWithRef<C, BubbleProps>;

const BubbleComponent = <C extends ElementType = 'li'>(
  {
    as,
    text,
    subText,
    children,
    bubbleIcon,
    onClick,
    contextActions,
    contextMenuReturnFocus,
    variant = 'text',
    src,
    imageProps,
    imageLoadError,
    pdfProps,
    videoProps,
    backgroundColor,
    styles,
    maxWidth,
    callProps,
    maxHeight,
    leadingIcon,
  }: PolymorphicBubbleProps<C>,
  ref: PolymorphicRef<C>
) => {
  const Component = as || 'li';
  const { direction, isSending } = useChatBubbleContext();
  const bubbleStyles = ChatStyles.bubble({
    direction,
    isImage: variant === 'image',
    isPdf: variant === 'pdf',
    isVideo: variant === 'video',
    backgroundColor,
    maxWidth,
    maxHeight,
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === KeyNames.Enter || event.key === KeyNames.Space) {
      onClick?.(event);
    }
  };

  if (isSending || (variant === 'image' && !src)) {
    return (
      <Component ref={ref} css={[bubbleStyles, styles, isSending && { opacity: 0.6 }]}>
        {variant === 'image' ? (
          imageLoadError && !isSending ? (
            <div
              css={{
                padding: theme.spacing(2),
              }}
            >
              <Icon name='warning-badge' color='error' />
              <Text data-testid='chat-image-error-message'>There was an error loading this image.</Text>
            </div>
          ) : (
            <SkeletonLoader
              width={IMAGE_SKELETON_SIZE.width}
              height={IMAGE_SKELETON_SIZE.height}
              animation='shimmer'
              data-testid='chat-image-skeleton-loader'
            />
          )
        ) : !text ? (
          <SpinningLoader size='medium' />
        ) : (
          <TextBubble text={text} subText={subText} variant='text' />
        )}
      </Component>
    );
  }

  if (!contextActions?.length) {
    return (
      <Component ref={ref} onClick={onClick} onKeyDown={handleKeyDown} tabIndex={0} css={[bubbleStyles, styles]}>
        {bubbleIcon}
        {variant === 'image' ? (
          <ImageBubble src={src} {...imageProps} />
        ) : variant === 'pdf' ? (
          <PdfBubble src={src} {...pdfProps} />
        ) : variant === 'video' ? (
          <VideoBubble src={src} {...videoProps} />
        ) : variant === 'call' ? (
          <CallBubble
            title={text}
            subtitle={subText}
            callResult={callProps?.callResult}
            callAction={callProps?.callAction}
            playAction={callProps?.playAction}
          >
            {children}
          </CallBubble>
        ) : variant === 'ai-activity' ? (
          <AiActivityBubble text={text} leadingIcon={leadingIcon} />
        ) : (
          <TextBubble text={text} subText={subText} variant={variant} />
        )}
      </Component>
    );
  }

  return (
    <ContextMenu actions={contextActions ?? []} returnFocus={contextMenuReturnFocus}>
      <Component ref={ref} onClick={onClick} onKeyDown={handleKeyDown} tabIndex={0} css={[bubbleStyles, styles]}>
        {bubbleIcon}
        {variant === 'image' ? (
          <ImageBubble src={src} {...imageProps} />
        ) : variant === 'pdf' ? (
          <PdfBubble src={src} {...pdfProps} />
        ) : variant === 'video' ? (
          <VideoBubble src={src} {...videoProps} />
        ) : variant === 'call' ? (
          <CallBubble
            title={text}
            subtitle={subText}
            callResult={callProps?.callResult}
            callAction={callProps?.callAction}
            playAction={callProps?.playAction}
          >
            {children}
          </CallBubble>
        ) : variant === 'ai-activity' ? (
          <AiActivityBubble text={text} leadingIcon={leadingIcon} />
        ) : (
          <TextBubble text={text} subText={subText} variant={variant} />
        )}
      </Component>
    </ContextMenu>
  );
};

export const Bubble = forwardRef(BubbleComponent);
Bubble.displayName = 'Bubble';
